import { supabase } from '@/lib/supabase'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Token refresh margin in seconds (5 minutes)
const TOKEN_REFRESH_MARGIN = 300

export class ApiError extends Error {
  status: number
  data?: unknown

  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

async function attemptTokenRefresh(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.refreshSession()
    if (error || !data.session) {
      return false
    }
    return true
  } catch {
    return false
  }
}

export async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  let { data: { session } } = await supabase.auth.getSession()

  // Proactively refresh if token expires in less than 5 minutes
  if (session?.expires_at) {
    const expiresIn = session.expires_at - Math.floor(Date.now() / 1000)
    if (expiresIn < TOKEN_REFRESH_MARGIN && expiresIn > 0) {
      const { data, error } = await supabase.auth.refreshSession()
      if (!error && data.session) {
        session = data.session
      }
    }
  }

  const token = session?.access_token

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (options.headers) {
    Object.assign(headers, options.headers)
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    const refreshed = await attemptTokenRefresh()
    if (refreshed) {
      const { data: { session: newSession } } = await supabase.auth.getSession()
      const newToken = newSession?.access_token
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`
      }
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      })
    } else {
      window.dispatchEvent(new CustomEvent('auth:logout'))
      await response.json().catch(() => ({}))
      throw new ApiError(401, 'Session expired. Please log in again.')
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    // Handle nested detail object (e.g., { detail: { message: "...", error_code: "..." } })
    const detail = errorData.detail
    const errorMessage =
      errorData.message ||
      (typeof detail === 'string' ? detail : detail?.message) ||
      'An error occurred'
    throw new ApiError(response.status, errorMessage, errorData)
  }

  // Handle 204 No Content responses
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null
  }

  // Only try to parse JSON if there's content
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  return null
}

